import { lazy, Suspense } from "react";
import type { ComponentProps } from "react";
import { loadSlateLib } from "@/extension/utils/lazy-lib";
import SlateEditorImpl from "./editor/SlateEditor";
import RenderUpdateImpl from "./editor/RenderUpdate";

/**
 * React.lazy boundaries over the slate EDITOR LIB LAYER.
 *
 * slate (core) + slate-react + slate-history are fetched on first render from
 * the lazy/slate.js WAR bundle (which shares app.js's React via the __BT_REACT__
 * bridge — see src/extension/utils/lazy-lib.ts). `loadSlateLib()` populates the
 * live-binding holder src/extension/utils/slate-runtime.ts BEFORE these factories
 * resolve, so the editor components (SlateEditor / RenderUpdate and their
 * children) — which read Editor / Transforms / Slate / Editable / … from that
 * holder — always see populated bindings by first render.
 *
 * The editor IMPLEMENTATIONS stay statically imported here (SlateEditorImpl /
 * RenderUpdateImpl), so they remain in app.js — only slate leaves. This mirrors
 * the calendar split's lib-layer scope (react-big-calendar leaves; the MUI/redux
 * app tree that consumes it stays). We do NOT `import('./editor/SlateEditor')`
 * dynamically: a webpack async chunk would be CSP-blocked in the WhatsApp Web
 * content script. The only async step is loadSlateLib's isolated-world native
 * import of the WAR bundle.
 *
 * Each wrapper embeds its own <Suspense> so call sites are drop-in replacements
 * for the eager components (no boundary plumbing needed upstream). The skeleton
 * is shown once, while slate loads; thereafter loadSlateLib is memoized and the
 * boundary resolves synchronously.
 */

const LazySlateEditorInner = lazy(async () => {
  await loadSlateLib();
  return { default: SlateEditorImpl };
});

const LazyRenderUpdateInner = lazy(async () => {
  await loadSlateLib();
  return { default: RenderUpdateImpl };
});

/** Lightweight placeholder while lazy/slate.js loads (one-time, then cached). */
const EditorSkeleton = ({ minHeight = 80 }: { minHeight?: number }) => (
  <div
    style={{
      width: "100%",
      minHeight,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "var(--tasks-text-secondary, #667781)",
      fontSize: 13,
      opacity: 0.7,
    }}
    aria-busy="true"
    aria-live="polite"
  >
    <span style={{ animation: "btSlatePulse 1.2s ease-in-out infinite" }}>Loading editor…</span>
    <style>{`@keyframes btSlatePulse { 0%,100% { opacity: .45 } 50% { opacity: .9 } }`}</style>
  </div>
);

/** Drop-in lazy replacement for ./editor/SlateEditor (same props). */
export default function SlateEditor(props: ComponentProps<typeof SlateEditorImpl>) {
  return (
    <Suspense fallback={<EditorSkeleton minHeight={120} />}>
      <LazySlateEditorInner {...props} />
    </Suspense>
  );
}

/** Drop-in lazy replacement for ./editor/RenderUpdate (same props). */
export function RenderUpdate(props: ComponentProps<typeof RenderUpdateImpl>) {
  return (
    <Suspense fallback={<EditorSkeleton minHeight={40} />}>
      <LazyRenderUpdateInner {...props} />
    </Suspense>
  );
}
